/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/**
 * SSSALON — minimal styling for Shopify's native Customer Privacy banner.
 * Presentation only; Shopify still handles consent actions/preferences.
 */
function installSSSalonCookieBannerStyles() {
  if (document.getElementById('SSSalonCookieBannerStyles')) return;

  const style = document.createElement('style');
  style.id = 'SSSalonCookieBannerStyles';
  style.textContent = `
    #shopify-pc__banner,
    #shopify-pc__banner.shopify-pc__banner__dialog {
      position: fixed !important;
      top: auto !important;
      right: 16px !important;
      bottom: 16px !important;
      left: 16px !important;
      width: auto !important;
      max-width: 720px !important;
      height: auto !important;
      min-height: 0 !important;
      max-height: none !important;
      margin: 0 auto !important;
      padding: 0 !important;
      border: 1px solid #e5e5e5 !important;
      border-radius: 22px !important;
      background: rgba(255,255,255,.985) !important;
      color: #111 !important;
      box-shadow: 0 16px 48px rgba(0,0,0,.12) !important;
      font-family: var(--font-body-family), Arial, sans-serif !important;
      resize: none !important;
      overflow: hidden !important;
      z-index: 2147483000 !important;
    }

    #shopify-pc__banner .shopify-pc__banner__dialog {
      position: static !important;
      inset: auto !important;
      width: 100% !important;
      max-width: none !important;
      height: auto !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      resize: none !important;
    }

    #shopify-pc__banner .shopify-pc__banner__wrapper {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 12px !important;
      width: 100% !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 18px 22px 15px !important;
      background: transparent !important;
      text-align: center !important;
    }

    #shopify-pc__banner .shopify-pc__banner__body {
      flex: 0 0 auto !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
      color: #111 !important;
      text-align: center !important;
    }

    #shopify-pc__banner #shopify-pc__banner__body-title,
    #shopify-pc__banner .shopify-pc__banner__body h2 {
      margin: 0 auto !important;
      padding: 0 !important;
      color: #111 !important;
      font-family: var(--font-body-family), Arial, sans-serif !important;
      font-size: 16px !important;
      font-style: normal !important;
      font-weight: 700 !important;
      line-height: 1.2 !important;
      letter-spacing: -.015em !important;
      text-align: center !important;
      text-transform: none !important;
    }

    #shopify-pc__banner .shopify-pc__banner__body p {
      display: none !important;
    }

    #shopify-pc__banner .shopify-pc__banner__btns,
    #shopify-pc__banner .shopify-pc__banner__btns-granular {
      flex: 0 0 auto !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-wrap: wrap !important;
      gap: 9px !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    #shopify-pc__banner .shopify-pc__banner__btns button,
    #shopify-pc__banner .shopify-pc__banner__btns-granular button {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      min-width: 0 !important;
      margin: 0 !important;
      box-shadow: none !important;
      outline: 0 !important;
      font-family: var(--font-body-family), Arial, sans-serif !important;
      font-style: normal !important;
      font-weight: 700 !important;
      line-height: 1 !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
      white-space: nowrap !important;
      transition: opacity .18s ease, transform .18s ease, background .18s ease !important;
    }

    #shopify-pc__banner #shopify-pc__banner__btn-accept,
    #shopify-pc__banner .shopify-pc__banner__btn-accept {
      order: 1 !important;
      min-width: 250px !important;
      min-height: 46px !important;
      height: 46px !important;
      padding: 0 24px !important;
      border: 1px solid #111 !important;
      border-radius: 999px !important;
      background: #111 !important;
      color: #fff !important;
      font-size: 13px !important;
    }

    #shopify-pc__banner #shopify-pc__banner__btn-decline,
    #shopify-pc__banner .shopify-pc__banner__btn-decline {
      order: 2 !important;
      min-width: 126px !important;
      min-height: 38px !important;
      height: 38px !important;
      padding: 0 16px !important;
      border: 1px solid #d9d9d9 !important;
      border-radius: 999px !important;
      background: #fff !important;
      color: #111 !important;
      font-size: 11.5px !important;
    }

    #shopify-pc__banner #shopify-pc__banner__btn-manage-prefs,
    #shopify-pc__banner .shopify-pc__banner__btn-manage-prefs {
      order: 3 !important;
      flex-basis: 100% !important;
      min-height: 22px !important;
      height: 22px !important;
      padding: 0 !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      color: #555 !important;
      font-size: 11px !important;
      font-weight: 600 !important;
      text-align: center !important;
      text-decoration: underline !important;
      text-decoration-thickness: 1px !important;
      text-underline-offset: 3px !important;
    }

    #shopify-pc__banner #shopify-pc__banner__btn-accept:hover,
    #shopify-pc__banner .shopify-pc__banner__btn-accept:hover {
      opacity: .9 !important;
    }

    #shopify-pc__banner #shopify-pc__banner__btn-decline:hover,
    #shopify-pc__banner .shopify-pc__banner__btn-decline:hover {
      background: #f7f7f7 !important;
    }

    #shopify-pc__banner button:focus-visible {
      outline: 2px solid #111 !important;
      outline-offset: 2px !important;
    }

    @media (max-width: 749px) {
      #shopify-pc__banner,
      #shopify-pc__banner.shopify-pc__banner__dialog {
        right: 10px !important;
        bottom: 10px !important;
        left: 10px !important;
        max-width: none !important;
        max-height: calc(100dvh - 20px) !important;
        border-radius: 18px !important;
        overflow-x: hidden !important;
        overflow-y: auto !important;
      }

      #shopify-pc__banner .shopify-pc__banner__wrapper {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 10px !important;
        padding: 13px 14px 11px !important;
        text-align: center !important;
      }

      #shopify-pc__banner .shopify-pc__banner__body {
        max-width: none !important;
        text-align: center !important;
      }

      #shopify-pc__banner #shopify-pc__banner__body-title,
      #shopify-pc__banner .shopify-pc__banner__body h2 {
        margin: 0 auto !important;
        font-size: 15px !important;
        line-height: 1.22 !important;
        text-align: center !important;
      }

      #shopify-pc__banner .shopify-pc__banner__btns,
      #shopify-pc__banner .shopify-pc__banner__btns-granular {
        display: grid !important;
        grid-template-columns: minmax(0, 1fr) auto !important;
        align-items: center !important;
        gap: 8px !important;
        width: 100% !important;
        margin: 0 !important;
      }

      #shopify-pc__banner #shopify-pc__banner__btn-accept,
      #shopify-pc__banner .shopify-pc__banner__btn-accept {
        width: 100% !important;
        min-width: 0 !important;
        min-height: 44px !important;
        height: 44px !important;
        padding: 0 12px !important;
        font-size: 12px !important;
      }

      #shopify-pc__banner #shopify-pc__banner__btn-decline,
      #shopify-pc__banner .shopify-pc__banner__btn-decline {
        width: auto !important;
        min-width: 104px !important;
        min-height: 38px !important;
        height: 38px !important;
        padding: 0 14px !important;
        font-size: 11px !important;
      }

      #shopify-pc__banner #shopify-pc__banner__btn-manage-prefs,
      #shopify-pc__banner .shopify-pc__banner__btn-manage-prefs {
        grid-column: 1 / -1 !important;
        width: 100% !important;
        min-height: 21px !important;
        height: 21px !important;
        margin-top: 0 !important;
        padding: 0 !important;
        font-size: 10.5px !important;
        text-align: center !important;
      }
    }
  `;

  document.head.appendChild(style);
}

installSSSalonCookieBannerStyles();
